import {
  BodyMedium,
  BodySmall,
  DeprecatedChevronDownIcon,
  HeadingSmall,
  LinkButton,
  theme,
  usePath,
} from '@arnold/common';
import styled from '@emotion/styled';
import { FC, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ReactComponent as ErrorWarningIcon } from '../../assets/images/ErrorWarning.svg';
import { DashboardSection } from './DashboardSection';

type PulserProps = {
  enabledRespondentsCount: number;
};

export const Pulser: FC<PulserProps> = ({ enabledRespondentsCount }) => {
  const { t } = useTranslation('dashboard');
  const history = useHistory();
  const { pulserTopicsPath, pulserConversationSubjectsPath } = usePath();
  const [showSteps, setShowSteps] = useState(true);
  const hasNoEmployees = enabledRespondentsCount <= 1;

  const steps = [
    {
      text: t('setupOrganizationStructure'),
      description: t('setupOrganizationStructureDescription'),
      button: t('addEmployees'),
      onClick: () => history.push('/teams'),
      icom: 'btn-pulser-add-employees',
      warning: hasNoEmployees ? t('noEmployeesAdded') : undefined,
    },
    {
      text: t('reviewSurveyQuestions'),
      description: t('reviewSurveyQuestionsDescription'),
      button: t('addAdditionalQuestion'),
      onClick: () => pulserTopicsPath.toFunc(),
      icom: 'btn-pulser-add-question',
    },
    {
      text: t('schedulePulserSurvey'),
      description: t('schedulePulserSurveyDescription'),
      button: t('newSurvey'),
      onClick: () => pulserConversationSubjectsPath.toFunc(),
      icom: 'btn-pulser-schedule',
    },
  ];

  const toggleSteps = () => setShowSteps((open) => !open);

  return (
    <DashboardSection keyName="pulser" title={t('pulser')} dataCy="dashboard-pulser">
      <StepsPanel data-cy="dashboard-pulser-steps">
        <StepsHeader>
          <StepsHeaderCopy>
            <HeadingSmall color={theme.colors.brand.primary} margin="0">
              {t('scheduleAPulserSurvey')}
            </HeadingSmall>
            <StepsSubtitle>
              <BodySmall margin="0" color={theme.colors.text.secondary}>
                {t('easilyIn3Steps')}
              </BodySmall>
              <LinkButton
                onClick={toggleSteps}
                data-icom="btn-pulser-toggle-steps"
                data-cy="btn-pulser-toggle-steps"
                aria-expanded={showSteps}
                margin="0"
                padding="0"
              >
                <StepsToggleLabel>
                  {showSteps ? t('hideSteps') : t('showSteps')}
                  <StyledChevron $expanded={showSteps} />
                </StepsToggleLabel>
              </LinkButton>
            </StepsSubtitle>
          </StepsHeaderCopy>
          {!showSteps && (
            <Button
              onClick={() => setShowSteps(true)}
              data-icom="btn-pulser-steps-to-schedule"
              data-cy="btn-pulser-steps-to-schedule"
            >
              {t('stepsToSchedule')}
            </Button>
          )}
        </StepsHeader>
        {showSteps && (
          <StepsList>
            {steps.map((step, index) => (
              <StepItem key={step.icom}>
                <StepCopy>
                  <StepNumber>{index + 1}</StepNumber>
                  <StepText>
                    <BodyMedium margin="0" bold>
                      {step.text}
                    </BodyMedium>
                    <BodySmall margin="0" color={theme.colors.text.secondary}>
                      {step.description}
                    </BodySmall>
                  </StepText>
                </StepCopy>
                <StepActions>
                  {step.warning && (
                    <NoEmployeesWarning>
                      <StyledWarningIcon />
                      <BodySmall margin="0" color={theme.colors.emotionDanger.default} bold>
                        {step.warning}
                      </BodySmall>
                    </NoEmployeesWarning>
                  )}
                  <Button onClick={step.onClick} data-icom={step.icom} data-cy={step.icom}>
                    {step.button}
                  </Button>
                </StepActions>
              </StepItem>
            ))}
          </StepsList>
        )}
      </StepsPanel>
    </DashboardSection>
  );
};

const StepsPanel = styled.div`
  flex: 1 1 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.backgroundCover.default};
  box-shadow: ${theme.shadows.elevationContentBlock};
  border-radius: ${theme.borderRadius.m};
  overflow: hidden;
`;

const StepsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.f};
  flex-wrap: wrap;
  padding: ${theme.spacing.f} ${theme.spacing.g};
`;

const StepsHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
`;

const StepsSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
`;

const StepsToggleLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.d};
`;

const StyledChevron = styled(DeprecatedChevronDownIcon)<{ $expanded?: boolean }>`
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.g};
  padding: ${theme.spacing.f} ${theme.spacing.g};
  border-top: 1px solid ${theme.colors.borderMain.default};
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing.f};
`;

const StepCopy = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.f};
  flex: 1 1 320px;
`;

const StepNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  margin-top: ${theme.spacing.b};
  border-radius: 50%;
  background-color: ${theme.colors.emotionInformative.background};
  color: ${theme.colors.brand.primary};
  font-weight: 700;
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
`;

const StepActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing.f};
  flex-wrap: wrap;
`;

const NoEmployeesWarning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
  color: ${theme.colors.emotionDanger.default};
`;

const StyledWarningIcon = styled(ErrorWarningIcon)`
  flex-shrink: 0;
`;
